
import { ArrowRight, Baby, Brain, Bug, Computer, Shield, Sparkles, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/LanguageContext";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import type { Dictionary } from "@/i18n/locales/hy";

type CourseKey = keyof Dictionary["courses"]["items"];
type BadgeKey = keyof Dictionary["courses"]["badges"];

const COURSES: { key: CourseKey; path: string; icon: LucideIcon; badge?: BadgeKey }[] = [
  { key: "aiTeacher", path: "/ai-teacher-training", icon: Sparkles, badge: "top2026" },
  { key: "cyber", path: "/cybersecurity", icon: Shield, badge: "top" },
  { key: "web", path: "/web-programming", icon: Computer },
  { key: "ai", path: "/ai", icon: Brain },
  { key: "kids", path: "/kids-programming", icon: Baby, badge: "new" },
  { key: "qa", path: "/courses/qa-testing", icon: Bug },
];

export const Courses = () => {
  const { t } = useI18n();

  return (
    <section id="courses" className="app-section scroll-mt-20 border-y border-border/40 bg-card/30">
      <div className="app-container">
        <Reveal className="mb-10 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div className="min-w-0">
            <span className="section-kicker">{t.courses.kicker}</span>
            <h2 className="section-title">{t.courses.title}</h2>
            <p className="section-copy">{t.courses.copy}</p>
          </div>
          <Button variant="outline" className="shrink-0" onClick={() => document.getElementById("faculties")?.scrollIntoView({ behavior: "smooth" })}>
            {t.courses.all}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Reveal>

        <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {COURSES.map((course, index) => {
            const Icon = course.icon;
            const copy = t.courses.items[course.key];
            return (
              <StaggerItem key={course.path} className="h-full">
                <article className="app-card group relative flex h-full min-h-[260px] flex-col overflow-hidden">
                  <div className="relative flex-1 p-6">
                    <div className="mb-8 flex items-start justify-between gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-primary/30 bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <Icon className="h-6 w-6" aria-hidden="true" />
                      </div>
                      {course.badge && <Badge className="mt-11 border border-primary/30 bg-secondary text-highlight">{t.courses.badges[course.badge]}</Badge>}
                    </div>
                    <h3 className="font-display text-xl font-bold leading-7 text-foreground">{copy.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">{copy.subtitle}</p>
                    <span className="pointer-events-none absolute right-5 top-5 font-display text-5xl font-bold text-primary/5" aria-hidden="true">
                      0{index + 1}
                    </span>
                  </div>
                  <div className="p-6 pt-0">
                    <Button asChild className="w-full">
                      <Link to={course.path} aria-label={`${copy.title} — ${t.courses.details}`}>
                        {t.courses.details}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
};
